import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import React, { useEffect, useState } from 'react';
import WindsorApi from '../../api/WindsorApi';
import LoadingIndicator from '../../common/LoadingIndicator';
import NoResultsText from '../../common/NoResultsText';
import CreateTestDialog from './CreateTestDialog';
import DownloadTestDialog from './DownloadTestDialog';
import EditTestDialog from './EditTestDialog';
import TestsTable from './TestsTable';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textFieldInput: {
      paddingTop: 12,
      paddingBottom: 12
    },
    toolbar: {
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    searchField: {
      width: 300
    },
    newTestPanel: {
      marginTop: 24,
      display: 'flex',
      justifyContent: 'center'
    }
  })
);

interface Props {
  familyId: string;
}

const windsorApi = new WindsorApi();

function Tests(props: Props & WithSnackbarProps) {
  const { familyId, enqueueSnackbar } = props;
  const classes = useStyles({});
  const [tests, setTests] = useState(null);
  const [search, setSearch] = useState('');
  const [showCreateTestDialog, setShowCreateTestDialog] = useState(false);
  const [testToEdit, setTestToEdit] = useState(null);
  const [testToDownload, setTestToDownload] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    windsorApi.getTests(familyId).then(tests => {
      setTests(tests);
    });
  }, [familyId]);


  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    // Reset page
    setPage(0);
  };

  const onCloseCreateTestDialog = (newTest: any) => {
    setShowCreateTestDialog(false);
    if (newTest) {
      setTests(prevTests => [...prevTests, newTest]);
      enqueueSnackbar(`Test ${newTest.name} created`, { variant: 'success' });
    }
  };

  const onOpenCreateTestDialog = () => {
    setShowCreateTestDialog(true);
  };

  const onCloseEditTestDialog = (updatedTest: any) => {
    setTestToEdit(null);
    if (updatedTest) {
      setTests(prevTests => {
        const testsCopy = prevTests.slice();
        const index = prevTests.findIndex(({ name }) => name === updatedTest.name);
        if (index !== -1) {
          testsCopy[index] = updatedTest;
        }
        return testsCopy;
      });
      enqueueSnackbar(`Test ${updatedTest.name} updated`, { variant: 'success' });
    }
  };

  const onCloseDownloadTestDialog = () => {
    setTestToDownload(null);
  };

  const onDownloadTest = (test: any) => {
    setTestToDownload(test);
  };

  const onEditTest = (test: any) => {
    setTestToEdit(test);
  };

  const normalizedSearch = search.toLowerCase();

  const filteredTests = tests ?
    tests.filter(test => !normalizedSearch || test.name.toLowerCase().includes(normalizedSearch)) : null;

  return (
    <div>
      {tests && tests.length > 0 && <div className={classes.toolbar}>
        <TextField
          className={classes.searchField}
          value={search}
          autoComplete="off"
          margin="none"
          variant="outlined"
          placeholder="Search by test name"
          inputProps={{ 'aria-label': 'bare', className: classes.textFieldInput }}
          onChange={onSearchChange}
        />
        <Button onClick={onOpenCreateTestDialog} variant="contained" color="primary">New Test</Button>
      </div>}
      {tests && tests.length === 0 && <div className={classes.newTestPanel}>
        <Button onClick={onOpenCreateTestDialog} variant="contained" color="primary">Create a Test</Button>
      </div>}
      {filteredTests && filteredTests.length > 0 && <Paper>
        <TestsTable
          tests={filteredTests}
          page={page}
          onPageChange={onPageChange}
          onEditTest={onEditTest}
          onDownloadTest={onDownloadTest}
        />
      </Paper>}
      {filteredTests && filteredTests.length === 0 && tests.length > 0 && <NoResultsText>No tests</NoResultsText>}
      <LoadingIndicator loading={!filteredTests} />
      {showCreateTestDialog && <CreateTestDialog familyId={familyId} onClose={onCloseCreateTestDialog} />}
      {testToEdit && <EditTestDialog familyId={familyId} test={testToEdit} onClose={onCloseEditTestDialog} />}
      {testToDownload && <DownloadTestDialog familyId={familyId} testName={testToDownload.name} onClose={onCloseDownloadTestDialog} />}
    </div>
  );
};

export default withSnackbar(Tests);
